/**
 * The progression tables (issue #9): which Choices the rules open at each Level. The class Entry
 * carries Foundry's feat and skill-increase level tables; attribute boosts, the Intelligence
 * skill trainings and the Variant Rules that add slots (#12: Free Archetype, Ancestry Paragon,
 * Gradual Attribute Boosts, Automatic Bonus Progression's skill potencies and apex, Dual Class)
 * follow the printed rules. `openProgression` makes the Character's log hold every expected
 * progression Choice up to its Level, so the guide can walk them.
 */
import type { Entry } from "../../shared/snapshot-format";
import { ensureLevels } from "../character/create";
import type { Character, Choice, LevelNumber, ProgressionSlot, VariantRules } from "../character/model";
import { LEVEL_NUMBERS, choiceKey } from "../character/model";
import { ABP_APEX_LEVEL, SKILL_POTENCY_STEPS } from "../engine/abp";
import { isAttribute, type Attribute } from "../engine/values";
import type { AttributeTrack } from "./attributes";

export const BOOST_LEVELS: readonly LevelNumber[] = [1, 5, 10, 15, 20];

/** The standard tables (Player Core), used until a class Entry supplies its own. */
export const DEFAULT_TABLES = Object.freeze({
  ancestryFeat: [1, 5, 9, 13, 17],
  classFeat: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
  skillFeat: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
  generalFeat: [3, 7, 11, 15, 19],
  skillIncrease: [3, 5, 7, 9, 11, 13, 15, 17, 19],
});

export interface ProgressionTables {
  ancestryFeat: number[];
  classFeat: number[];
  /** Dual Class (#12): class-feat Levels the second class opens that the first does not; empty otherwise. */
  dualClassFeat: number[];
  skillFeat: number[];
  generalFeat: number[];
  skillIncrease: number[];
  /** Free skill trainings at Level 1, before the Intelligence modifier is added. */
  additionalSkills: number;
  /** The key attribute options the class offers (both classes' under Dual Class). */
  keyOptions: Attribute[];
  /**
   * Familiar ability Choices opened at each Level (index is the Level; #13): the slots the
   * master's Entries add at that Level less the fixed abilities arriving then, from the engine's
   * slot track. Empty when nothing gives the Character a Familiar.
   */
  familiarSlots: number[];
  /** Apparition Choices opened at each Level (index is the Level; #21): two at Apparition Attunement, one more at Third and Fourth Apparition. */
  apparitionSlots: number[];
  /** Whether a class Entry supplied these (false: the defaults). */
  fromClass: boolean;
}

function levelsOf(value: unknown, fallback: number[]): number[] {
  const list = (value as { value?: unknown } | undefined)?.value;
  if (!Array.isArray(list)) return [...fallback];
  return [...new Set(list.filter((n): n is number => typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 20))].sort((a, b) => a - b);
}

/**
 * The tables a class Entry carries (Foundry: `ClassPF2e#grantedFeatSlots` and
 * `system.skillIncreaseLevels`). Under Dual Class (#12) the second class merges in: the better
 * of the two progressions at every Level (the union of the Levels), one class feat per Level from
 * either class, the larger number of free skill trainings, the key attribute options of both.
 */
export function progressionTables(classEntry: Entry | undefined, secondClass?: Entry | undefined): ProgressionTables {
  const s = classEntry?.system ?? {};
  const trained = s.trainedSkills as { additional?: unknown } | undefined;
  const key = s.keyAbility as { value?: unknown } | undefined;
  const own: ProgressionTables = {
    ancestryFeat: levelsOf(s.ancestryFeatLevels, DEFAULT_TABLES.ancestryFeat),
    classFeat: classEntry ? levelsOf(s.classFeatLevels, DEFAULT_TABLES.classFeat) : [],
    dualClassFeat: [],
    skillFeat: levelsOf(s.skillFeatLevels, DEFAULT_TABLES.skillFeat),
    generalFeat: levelsOf(s.generalFeatLevels, DEFAULT_TABLES.generalFeat),
    skillIncrease: levelsOf(s.skillIncreaseLevels, DEFAULT_TABLES.skillIncrease),
    additionalSkills: typeof trained?.additional === "number" ? trained.additional : 0,
    keyOptions: Array.isArray(key?.value) ? key.value.filter(isAttribute) : [],
    familiarSlots: [],
    apparitionSlots: [],
    fromClass: !!classEntry,
  };
  if (!secondClass) return own;
  const other = progressionTables(secondClass);
  const union = (a: number[], b: number[]): number[] => [...new Set([...a, ...b])].sort((x, y) => x - y);
  return {
    ancestryFeat: union(own.ancestryFeat, other.ancestryFeat),
    classFeat: own.classFeat,
    dualClassFeat: other.classFeat.filter((l) => !own.classFeat.includes(l)),
    skillFeat: union(own.skillFeat, other.skillFeat),
    generalFeat: union(own.generalFeat, other.generalFeat),
    skillIncrease: union(own.skillIncrease, other.skillIncrease),
    additionalSkills: Math.max(own.additionalSkills, other.additionalSkills),
    keyOptions: [...new Set([...own.keyOptions, ...other.keyOptions])],
    familiarSlots: [],
    apparitionSlots: [],
    fromClass: own.fromClass || other.fromClass,
  };
}

/** How many free attribute boosts a Level opens: four at 1, 5, 10, 15 and 20, or one per Level in the four Levels up to each of those under Gradual Attribute Boosts (Foundry: `allowedBoosts`). */
export function boostsOpenedAt(level: LevelNumber, gradual: boolean): number {
  if (level === 1) return 4;
  if (!gradual) return BOOST_LEVELS.includes(level) ? 4 : 0;
  return BOOST_LEVELS.some((bucket) => level > bucket - 4 && level <= bucket) ? 1 : 0;
}

/** The boost set a Level's boosts belong to (the bucket the engine files them under): no attribute may be boosted twice within it. */
export function boostBucket(level: LevelNumber): LevelNumber {
  return BOOST_LEVELS.find((b) => b >= level) ?? 20;
}

export interface ExpectedSlot {
  slot: ProgressionSlot;
  ordinal: number;
  /** A skill increase that the Intelligence modifier's rise opened: it trains an untrained skill only. */
  intelligence?: true;
}

/** Levels at which Ancestry Paragon adds an ancestry feat (GM Core: 1st, then 3rd and every four Levels). */
const PARAGON_LEVELS = [1, 3, 7, 11, 15, 19];

/** The progression Choices the rules open at one Level, in the guide's order. */
export function expectedSlots(level: LevelNumber, tables: ProgressionTables, variant: VariantRules, intelligenceIncrease: boolean): ExpectedSlot[] {
  const out: ExpectedSlot[] = [];
  const add = (slot: ProgressionSlot, count: number, extra?: { intelligence?: true }): void => {
    const start = out.filter((s) => s.slot === slot).length;
    for (let i = 0; i < count; i++) out.push({ slot, ordinal: start + i, ...(extra ?? {}) });
  };
  add("attribute-boosts", boostsOpenedAt(level, variant.gradualAttributeBoosts));
  if (variant.automaticBonusProgression && level === ABP_APEX_LEVEL) add("attribute-apex", 1);
  if (tables.classFeat.includes(level)) add("class-feat", 1);
  else if (variant.dualClass && tables.dualClassFeat.includes(level)) add("dual-class-feat", 1);
  if (tables.ancestryFeat.includes(level)) add("ancestry-feat", 1);
  if (variant.ancestryParagon && PARAGON_LEVELS.includes(level)) add("ancestry-paragon-feat", 1);
  if (tables.skillFeat.includes(level)) add("skill-feat", 1);
  if (tables.generalFeat.includes(level)) add("general-feat", 1);
  if (tables.skillIncrease.includes(level)) add("skill-increase", 1);
  if (intelligenceIncrease && level > 1) add("skill-increase", 1, { intelligence: true });
  if (variant.automaticBonusProgression) add("skill-potency", SKILL_POTENCY_STEPS[level]?.length ?? 0);
  if (variant.freeArchetype && level % 2 === 0) add("archetype-feat", 1);
  add("familiar-ability", tables.familiarSlots[level] ?? 0);
  add("apparition", tables.apparitionSlots[level] ?? 0);
  return out;
}

/** The progression slots a Variant Rule opens (#12): a toggle removes their Choices, filled or not. */
export const VARIANT_SLOTS: Record<Extract<ProgressionSlot, "archetype-feat" | "ancestry-paragon-feat" | "dual-class-feat" | "skill-potency" | "attribute-apex">, keyof VariantRules> = {
  "archetype-feat": "freeArchetype",
  "ancestry-paragon-feat": "ancestryParagon",
  "dual-class-feat": "dualClass",
  "skill-potency": "automaticBonusProgression",
  "attribute-apex": "automaticBonusProgression",
};

/** Whether the Intelligence modifier rises at this Level (its boosts included), so one more skill training opens (Player Core, "Attribute Boosts"). */
export function intelligenceRises(track: AttributeTrack, level: LevelNumber): boolean {
  if (level === 1) return false;
  return track.modAt(level).int > track.modAt((level - 1) as LevelNumber).int;
}

/** Whether a progression Choice holds an answer rather than a Selection (boosts, skill increases, skill potencies, the apex). */
export function isAnswerSlot(slot: ProgressionSlot): boolean {
  return slot === "attribute-boosts" || slot === "skill-increase" || slot === "skill-potency" || slot === "attribute-apex";
}

export interface OpenedProgression {
  character: Character;
  changed: boolean;
  /** Filled progression Choices the current tables do not open (a slot from a former class): kept, shown with a warning. */
  extra: { level: LevelNumber; key: string }[];
}

/**
 * Add the progression Choices every Level up to the Character's Level should hold and drop empty
 * ones the tables no longer open. Filled ones are never removed here: a Selection the tables no
 * longer expect is reported as `extra` and reopened by the change flow when it is illegal (a
 * Variant Rule toggle removes the ones its rule owned, see ./variants.ts). The Dual Class second
 * class Choice opens at Level 1 with the rule and closes with it once empty.
 */
export function openProgression(input: Character, tables: ProgressionTables, track: AttributeTrack): OpenedProgression {
  const character = input.levels.length < input.level ? ensureLevels(input, input.level) : input;
  let changed = character !== input;
  const extra: OpenedProgression["extra"] = [];
  const levels = character.levels.map((level) => {
    if (level.number > character.level) return level;
    const expected = expectedSlots(level.number, tables, character.variantRules, intelligenceRises(track, level.number));
    const expectedKeys = new Set(expected.map((s) => choiceKey(level.number, { kind: "progression", slot: s.slot, ordinal: s.ordinal })));
    const kept: Choice[] = [];
    let touched = false;
    for (const choice of level.choices) {
      if (choice.source.kind === "dual-class") {
        if (character.variantRules.dualClass) kept.push(choice);
        else if (choice.selection) {
          kept.push(choice);
          extra.push({ level: level.number, key: choiceKey(level.number, choice.source) });
        } else touched = true;
        continue;
      }
      if (choice.source.kind !== "progression") {
        kept.push(choice);
        continue;
      }
      const key = choiceKey(level.number, choice.source);
      if (expectedKeys.has(key)) {
        kept.push(choice);
        continue;
      }
      const filled = !!choice.selection || (isAnswerSlot(choice.source.slot) && Object.keys(choice.answers ?? {}).length > 0);
      if (filled) {
        kept.push(choice);
        extra.push({ level: level.number, key });
      } else touched = true;
    }
    const held = new Set(kept.filter((c) => c.source.kind === "progression").map((c) => choiceKey(level.number, c.source)));
    for (const slot of expected) {
      const source = { kind: "progression" as const, slot: slot.slot, ordinal: slot.ordinal };
      if (held.has(choiceKey(level.number, source))) continue;
      kept.push({ source });
      touched = true;
    }
    if (level.number === 1 && character.variantRules.dualClass && !kept.some((c) => c.source.kind === "dual-class")) {
      // After the class it pairs with, before the progression slots.
      const at = kept.findIndex((c) => c.source.kind === "class");
      kept.splice(at === -1 ? kept.length : at + 1, 0, { source: { kind: "dual-class" } });
      touched = true;
    }
    if (!touched) return level;
    changed = true;
    return { number: level.number, choices: kept };
  });
  return { character: changed ? { ...character, levels } : input, changed, extra };
}

export { LEVEL_NUMBERS };
